import { MemberOrder } from '../../auth/memberApi';
import './index.css';

const pad = (value: number) => String(value).padStart(2, '0');

const parseOrderDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatOrderDate = (value: string) => {
  const date = parseOrderDate(value);
  if (!date) return 'Date unavailable';
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatDate = (date: Date) =>
  `${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${date.getFullYear()}`;

const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const formatMoney = (value: number) => `$${Number(value || 0).toFixed(2)}`;

const getFulfillmentEstimate = (orderDate: string) => {
  const placed = parseOrderDate(orderDate);
  if (!placed) {
    return { shipment: 'Unavailable', delivery: 'Unavailable' };
  }
  const shipment = addDays(placed, 2);
  return {
    shipment: `By ${formatDate(shipment)}`,
    delivery: `${formatDate(addDays(shipment, 3))} – ${formatDate(addDays(shipment, 5))}`,
  };
};

interface OrderCardsProps {
  orders: MemberOrder[];
  emptyTitle?: string;
  emptyMessage?: string;
  onCreateInvoice?: (order: MemberOrder) => void;
  onUploadInvoice?: (order: MemberOrder, files: File[]) => void;
  onDownloadInvoice?: (order: MemberOrder) => void;
  invoiceBusyOrderId?: string;
}

const OrderCards = ({
  orders,
  emptyTitle = 'No orders yet.',
  emptyMessage = 'Your completed Hogcloud purchases will appear here automatically.',
  onCreateInvoice,
  onUploadInvoice,
  onDownloadInvoice,
  invoiceBusyOrderId,
}: OrderCardsProps) => (
  <div className="customer-orders-list" aria-label="Customer order list">
    {orders.map((order) => {
      const estimate = getFulfillmentEstimate(order.orderDate);
      return (
        <article className="customer-order-card" key={order.orderId}>
          <div className="customer-order-main">
            <div className="customer-order-heading" aria-label="Order date and total">
              <span>
                <small>Date</small>
                <strong>{formatOrderDate(order.orderDate)}</strong>
              </span>
              <span>
                <small>Total</small>
                <strong>{formatMoney(order.orderTotal)}</strong>
              </span>
            </div>
            <span className="customer-order-status">{order.status || 'Processing'}</span>
          </div>

          <p className="customer-order-number">Order {order.orderId}</p>

          <div className="customer-order-fulfillment">
            <span><strong>Expected shipment</strong><em>{estimate.shipment}</em></span>
            <span><strong>Expected delivery</strong><em>{estimate.delivery}</em></span>
          </div>

          <div className="customer-order-items">
            <h3>Items ordered</h3>
            {order.items.length ? (
              <ul>
                {order.items.map((item, index) => (
                  <li key={`${order.orderId}-${item.itemId}-${index}`}>
                    <div>
                      <strong>{item.quantity} × {item.name}</strong>
                      {item.customizationItems?.length > 0 && (
                        <span>
                          {item.customizationItems
                            .map((customization) => `${customization.name}: ${customization.customText}`)
                            .join(', ')}
                        </span>
                      )}
                    </div>
                    <em>{formatMoney(item.totalPrice)}</em>
                  </li>
                ))}
              </ul>
            ) : <p>No line-item details are available for this order.</p>}
          </div>

          {(onCreateInvoice || onUploadInvoice || onDownloadInvoice) && (
            <div className="customer-order-invoice">
              <div>
                <strong>Invoice</strong>
                <span>{order.invoice ? 'Invoice documents are available.' : 'No invoice has been added.'}</span>
              </div>
              <div className="customer-order-invoice-actions">
                {onCreateInvoice && !order.invoice && (
                  <button
                    type="button"
                    onClick={() => onCreateInvoice(order)}
                    disabled={invoiceBusyOrderId === order.orderId}
                  >
                    {invoiceBusyOrderId === order.orderId ? 'Creating…' : 'Create'}
                  </button>
                )}
                {onUploadInvoice && order.invoice && (
                  <label>
                    <span>{invoiceBusyOrderId === order.orderId ? 'Uploading…' : 'Upload'}</span>
                    <input
                      type="file"
                      multiple
                      disabled={invoiceBusyOrderId === order.orderId}
                      onChange={(event) => {
                        const files = Array.from(event.target.files || []);
                        if (files.length) onUploadInvoice(order, files);
                        event.target.value = '';
                      }}
                    />
                  </label>
                )}
                {onDownloadInvoice && order.invoice && (
                  <button
                    type="button"
                    onClick={() => onDownloadInvoice(order)}
                    disabled={invoiceBusyOrderId === order.orderId}
                  >
                    {invoiceBusyOrderId === order.orderId ? 'Preparing…' : 'Download invoice'}
                  </button>
                )}
              </div>
            </div>
          )}
        </article>
      );
    })}

    {orders.length === 0 && (
      <div className="customer-orders-empty">
        <h2>{emptyTitle}</h2>
        <p>{emptyMessage}</p>
      </div>
    )}
  </div>
);

export default OrderCards;
